import { lazy, Suspense, useRef } from "react";
import Footer from "./components/Footer";
import FloatingWhatsAppButton from "./components/FloatingWhatsAppButton";
import Header from "./components/Header";
import PageTransition from "./components/PageTransition";
import ScrollProgress from "./components/ScrollProgress";
import SiteBackdrop from "./components/SiteBackdrop";
import { useReveal } from "./hooks/useReveal";
import { useRoute } from "./hooks/useRoute";
import { useScrollProgress } from "./hooks/useScrollProgress";
import { useTheme } from "./hooks/useTheme";

const loadAboutPage = () => import("./pages/AboutPage");
const loadAccessPage = () => import("./pages/AccessPage");
const loadBoxesPage = () => import("./pages/BoxesPage");
const loadCareersPage = () => import("./pages/CareersPage");
const loadCaseStudiesPage = () => import("./pages/CaseStudiesPage");
const loadClientsPage = () => import("./pages/ClientsPage");
const loadContactPage = () => import("./pages/ContactPage");
const loadFaqPage = () => import("./pages/FaqPage");
const loadGetStartedPage = () => import("./pages/GetStartedPage");
const loadHomePage = () => import("./pages/HomePage");
const loadInsightsPage = () => import("./pages/InsightsPage");
const loadInsurancePage = () => import("./pages/InsurancePage");
const loadLoginPage = () => import("./pages/LoginPage");
const loadNotFoundPage = () => import("./pages/NotFoundPage");
const loadPaymentPolicyPage = () => import("./pages/PaymentPolicyPage");
const loadPricingPage = () => import("./pages/PricingPage");
const loadPrivacyPolicyPage = () => import("./pages/PrivacyPolicyPage");
const loadSecurityPage = () => import("./pages/SecurityPage");
const loadSolutionsPage = () => import("./pages/SolutionsPage");
const loadSupportPage = () => import("./pages/SupportPage");
const loadTrackingPage = () => import("./pages/TrackingPage");

const AboutPage = lazy(loadAboutPage);
const AccessPage = lazy(loadAccessPage);
const BoxesPage = lazy(loadBoxesPage);
const CareersPage = lazy(loadCareersPage);
const CaseStudiesPage = lazy(loadCaseStudiesPage);
const ClientsPage = lazy(loadClientsPage);
const ContactPage = lazy(loadContactPage);
const FaqPage = lazy(loadFaqPage);
const GetStartedPage = lazy(loadGetStartedPage);
const HomePage = lazy(loadHomePage);
const InsightsPage = lazy(loadInsightsPage);
const InsurancePage = lazy(loadInsurancePage);
const LoginPage = lazy(loadLoginPage);
const NotFoundPage = lazy(loadNotFoundPage);
const PaymentPolicyPage = lazy(loadPaymentPolicyPage);
const PricingPage = lazy(loadPricingPage);
const PrivacyPolicyPage = lazy(loadPrivacyPolicyPage);
const SecurityPage = lazy(loadSecurityPage);
const SolutionsPage = lazy(loadSolutionsPage);
const SupportPage = lazy(loadSupportPage);
const TrackingPage = lazy(loadTrackingPage);

const routeMap = {
  "/": HomePage,
  "/about": AboutPage,
  "/about.html": AboutPage,
  "/access": AccessPage,
  "/how-it-works": AccessPage,
  "/boxes": BoxesPage,
  "/boxes-branches": BoxesPage,
  "/boxes-branches.html": BoxesPage,
  "/careers": CareersPage,
  "/case-studies": CaseStudiesPage,
  "/clients": ClientsPage,
  "/coverage": InsurancePage,
  "/faqs": FaqPage,
  "/faqs.html": FaqPage,
  "/get-started": GetStartedPage,
  "/book-now": GetStartedPage,
  "/booknow.html": GetStartedPage,
  "/governance": SecurityPage,
  "/insights": InsightsPage,
  "/insurance": InsurancePage,
  "/insurance.html": InsurancePage,
  "/login": LoginPage,
  "/payment-policy": PaymentPolicyPage,
  "/payment_policy.html": PaymentPolicyPage,
  "/pricing": PricingPage,
  "/privacy-policy": PrivacyPolicyPage,
  "/privacy_policy.html": PrivacyPolicyPage,
  "/security": SecurityPage,
  "/solutions": SolutionsPage,
  "/support": SupportPage,
  "/tracking": TrackingPage,
  "/tracking.html": TrackingPage,
  "/contact": ContactPage,
  "/contact.html": ContactPage,
};

const routeLoaders = {
  "/": loadHomePage,
  "/about": loadAboutPage,
  "/about.html": loadAboutPage,
  "/access": loadAccessPage,
  "/how-it-works": loadAccessPage,
  "/boxes": loadBoxesPage,
  "/boxes-branches": loadBoxesPage,
  "/boxes-branches.html": loadBoxesPage,
  "/careers": loadCareersPage,
  "/case-studies": loadCaseStudiesPage,
  "/clients": loadClientsPage,
  "/coverage": loadInsurancePage,
  "/faqs": loadFaqPage,
  "/faqs.html": loadFaqPage,
  "/get-started": loadGetStartedPage,
  "/book-now": loadGetStartedPage,
  "/booknow.html": loadGetStartedPage,
  "/governance": loadSecurityPage,
  "/insights": loadInsightsPage,
  "/insurance": loadInsurancePage,
  "/insurance.html": loadInsurancePage,
  "/login": loadLoginPage,
  "/payment-policy": loadPaymentPolicyPage,
  "/payment_policy.html": loadPaymentPolicyPage,
  "/pricing": loadPricingPage,
  "/privacy-policy": loadPrivacyPolicyPage,
  "/privacy_policy.html": loadPrivacyPolicyPage,
  "/security": loadSecurityPage,
  "/solutions": loadSolutionsPage,
  "/support": loadSupportPage,
  "/tracking": loadTrackingPage,
  "/tracking.html": loadTrackingPage,
  "/contact": loadContactPage,
  "/contact.html": loadContactPage,
};

const preloadRoute = (path) => {
  routeLoaders[path]?.();
};

function RouteFallback() {
  return (
    <div
      className="min-h-[70vh]"
      aria-label="Loading page"
      aria-live="polite"
      role="status"
    />
  );
}

function RouteContent({ Page, path }) {
  const routeRef = useRef(null);

  useReveal(path, routeRef);

  return (
    <div ref={routeRef}>
      <Page />
    </div>
  );
}

function App() {
  const path = useRoute({ preloadRoute });
  const { theme, toggleTheme } = useTheme();

  useScrollProgress();

  const Page = routeMap[path] || NotFoundPage;

  return (
    <div className={`${theme === "dark" ? "dark" : ""} bg-mist transition-colors duration-500 dark:bg-outerspace`}>
      <div className="relative isolate min-h-screen overflow-hidden text-ink transition-colors duration-500 dark:text-white">
        <SiteBackdrop />
        <PageTransition path={path} />
        <div className="relative z-10">
          <ScrollProgress />
          <Header
            currentPath={path}
            theme={theme}
            toggleTheme={toggleTheme}
          />
          <main>
            <Suspense fallback={<RouteFallback />}>
              <RouteContent Page={Page} path={path} />
            </Suspense>
          </main>
          <Footer />
          <FloatingWhatsAppButton />
        </div>
      </div>
    </div>
  );
}

export default App;
